/**
 * Descargas AUTENTICADAS de archivos (PDF / Excel) protegidos por el backend.
 *
 * Una navegación (window.open, window.location, <a href>) no envía la
 * cabecera Authorization, así que las rutas protegidas responden 401. Aquí
 * la petición la hace apiClient (que inyecta el Bearer) con responseType
 * 'blob'; el token nunca viaja en la URL. Se valida que la respuesta sea de
 * verdad el archivo esperado, para no guardar nunca un error como .pdf/.xlsx.
 *
 * Las funciones puras (nombres, validación, mensajes) se prueban con Node
 * liso: ver src/utils/descargaAutenticada.test.mjs.
 */

const TIPOS = {
  excel: {
    extension: 'xlsx',
    contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    magic: [0x50, 0x4b] // un .xlsx es un ZIP: "PK"
  },
  pdf: {
    extension: 'pdf',
    contentType: 'application/pdf',
    magic: [0x25, 0x50, 0x44, 0x46] // "%PDF"
  }
};

export function extensionPorTipo(tipo) {
  return TIPOS[tipo]?.extension || null;
}

const esFechaISO = (f) => typeof f === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(f);

/**
 * Nombre de archivo según el PERIODO consultado (no la fecha de generación):
 *   MIDAS_<prefijo>_2026-09-29.xlsx
 *   MIDAS_<prefijo>_2026-09-01_al_2026-09-30.pdf
 */
export function nombreArchivoPeriodo(prefijo, tipo, fechaDesde, fechaHasta) {
  const ext = extensionPorTipo(tipo);
  if (!ext) throw new Error(`Tipo de archivo desconocido: ${tipo}`);
  if (!esFechaISO(fechaDesde) || !esFechaISO(fechaHasta)) {
    throw new Error('Periodo no disponible para nombrar el archivo.');
  }
  const periodo = fechaDesde === fechaHasta ? fechaDesde : `${fechaDesde}_al_${fechaHasta}`;
  return `MIDAS_${prefijo}_${periodo}.${ext}`;
}

/** Quita valores vacíos y los comodines "TODOS"/"TODAS". */
export function limpiarParametros(params = {}) {
  const limpio = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '' && v !== 'TODOS' && v !== 'TODAS') {
      limpio[k] = Array.isArray(v) ? v.join(',') : v;
    }
  });
  return limpio;
}

/**
 * Decide si una respuesta 2xx es de verdad el archivo esperado.
 * @param {string} tipo 'excel' | 'pdf'
 * @param {string} contentType cabecera Content-Type recibida
 * @param {Uint8Array} primerosBytes primeros bytes del cuerpo
 */
export function validarArchivoDescargado(tipo, contentType, primerosBytes) {
  const cfg = TIPOS[tipo];
  if (!cfg) return { ok: false, motivo: 'Tipo de archivo desconocido.' };
  const ct = String(contentType || '').toLowerCase();
  if (!ct.startsWith(cfg.contentType)) {
    return { ok: false, motivo: 'El servidor no devolvió un archivo válido.' };
  }
  const bytes = primerosBytes || new Uint8Array(0);
  if (!cfg.magic.every((b, i) => bytes[i] === b)) {
    return { ok: false, motivo: 'El archivo recibido está dañado o no tiene el formato esperado.' };
  }
  return { ok: true };
}

/** Mensaje para el usuario según el estado HTTP (0 = sin conexión / timeout). */
export function mensajeErrorDescarga(status, mensajeServidor) {
  if (status === 401) return 'Tu sesión expiró. Inicia sesión de nuevo para descargar el documento.';
  if (status === 403) return 'No tienes permiso para descargar este documento.';
  if (!status) return 'No se pudo conectar con el servidor. Revisa tu conexión e inténtalo de nuevo.';
  return mensajeServidor || 'No se pudo generar el archivo. Inténtalo de nuevo.';
}

/**
 * Ejecuta la petición autenticada y devuelve el Blob ya validado.
 * Lanza Error con un mensaje legible para el usuario ante 401/403/red/archivo
 * inválido. `solicitar` debe devolver la respuesta axios (responseType blob).
 */
export async function obtenerArchivoAutenticado(tipo, solicitar) {
  try {
    const res = await solicitar();
    const blob = res.data;
    const primerosBytes = new Uint8Array(await blob.slice(0, 8).arrayBuffer());
    const validacion = validarArchivoDescargado(tipo, res.headers?.['content-type'], primerosBytes);
    if (!validacion.ok) throw new Error(validacion.motivo);
    return new Blob([blob], { type: blob.type || res.headers?.['content-type'] });
  } catch (err) {
    if (!err?.isAxiosError) throw err; // fallo de validación propio (mensaje ya legible)
    let mensajeServidor = null;
    const cuerpo = err.response?.data;
    if (typeof Blob !== 'undefined' && cuerpo instanceof Blob) {
      try { mensajeServidor = JSON.parse(await cuerpo.text())?.error || null; } catch { /* cuerpo no JSON */ }
    }
    throw new Error(mensajeErrorDescarga(err.response?.status || 0, mensajeServidor));
  }
}

/** Dispara la descarga de un Blob con el nombre dado y libera la URL temporal. */
export function guardarBlob(blob, nombre) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

/**
 * Imprime un PDF (Blob) a través de un iframe oculto; el PDF impreso es
 * exactamente el documento descargado, no la pantalla. Si el navegador no
 * permite imprimirlo incrustado, lo abre en una pestaña (blob local).
 */
export function imprimirBlobPdf(blob) {
  const url = URL.createObjectURL(blob);
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  const limpiar = () => {
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    URL.revokeObjectURL(url);
  };
  iframe.onload = () => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch {
      window.open(url, '_blank');
      setTimeout(limpiar, 60000);
      return;
    }
    setTimeout(limpiar, 60000);
  };
  iframe.src = url;
  document.body.appendChild(iframe);
}
